import client from "@/client/publicApiClient.ts";
import { useMutation } from "@tanstack/react-query";
import type { CreateClientMarketTemplateMutationRequest, CreateClientMarketTemplateMutationResponse, CreateClientMarketTemplatePathParams, CreateClientMarketTemplate400, CreateClientMarketTemplate404 } from "../types/CreateClientMarketTemplate";
import type { UseMutationOptions } from "@tanstack/react-query";

 type CreateClientMarketTemplateClient = typeof client<CreateClientMarketTemplateMutationResponse, CreateClientMarketTemplate400 | CreateClientMarketTemplate404, CreateClientMarketTemplateMutationRequest>;
type CreateClientMarketTemplate = {
    data: CreateClientMarketTemplateMutationResponse;
    error: CreateClientMarketTemplate400 | CreateClientMarketTemplate404;
    request: CreateClientMarketTemplateMutationRequest;
    pathParams: CreateClientMarketTemplatePathParams;
    queryParams: never;
    headerParams: never;
    response: CreateClientMarketTemplateMutationResponse;
    client: {
        parameters: Partial<Parameters<CreateClientMarketTemplateClient>[0]>;
        return: Awaited<ReturnType<CreateClientMarketTemplateClient>>;
    };
};
/**
 * @description Create a template from an existing market. Can optionally create the depth
as per the order book of the origin market at time of creation.
 * @summary Create template
 * @link /api/clients/:clientCode/markets/templates
 */
export function useCreateClientMarketTemplate(clientCode: CreateClientMarketTemplatePathParams["clientCode"], options: {
    mutation?: UseMutationOptions<CreateClientMarketTemplate["response"], CreateClientMarketTemplate["error"], CreateClientMarketTemplate["request"]>;
    client?: CreateClientMarketTemplate["client"]["parameters"];
} = {}) {
    const { mutation: mutationOptions, client: clientOptions = {} } = options ?? {};
    return useMutation({
        mutationFn: async (data) => {
            const res = await client<CreateClientMarketTemplate["data"], CreateClientMarketTemplate["error"], CreateClientMarketTemplate["request"]>({
                method: "post",
                url: `/api/clients/${clientCode}/markets/templates`,
                data,
                headers: { "Content-Type": "text/json", ...clientOptions.headers },
                ...clientOptions
            });
            return res.data;
        },
        ...mutationOptions
    });
}